import React, { useState, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  Badge,
  Button,
  HStack,
  VStack,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { Appointment, Physician, Location } from "../types";

interface AppointmentCardProps {
  appointment: Appointment;
  physicians: Physician[];
  locations: Location[];
  onCancel: (appointmentId: string) => void;
  onCheckIn: (appointmentId: string) => void;
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  physicians,
  locations,
  onCancel,
  onCheckIn,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const physician = physicians.find((p) => p.id === appointment.physicianId);
  const location = locations.find((l) => l.id === appointment.locationId);

  const startTime = new Date(appointment.startTime);
  const endTime = new Date(appointment.endTime);

  const isUpcoming = startTime > new Date();
  const isToday = startTime.toDateString() === new Date().toDateString();

  // Check if appointment is within 15 minutes of current time (for check-in)
  const canCheckIn =
    isToday &&
    startTime.getTime() - new Date().getTime() <= 15 * 60 * 1000 &&
    startTime.getTime() > new Date().getTime();

  return (
    <>
      <Box
        borderWidth="1px"
        borderRadius="lg"
        p={4}
        mb={4}
        borderColor={isUpcoming ? "blue.200" : "gray.200"}
        bg={isUpcoming ? "blue.50" : "white"}
      >
        <VStack align="stretch" gap={3}>
          <HStack justify="space-between">
            <Heading size="sm">
              {format(startTime, "EEEE, MMMM d, yyyy")}
            </Heading>
            <Badge
              colorScheme={
                appointment.status === "confirmed"
                  ? "green"
                  : appointment.status === "cancelled"
                  ? "red"
                  : "gray"
              }
            >
              {appointment.status.charAt(0).toUpperCase() +
                appointment.status.slice(1)}
            </Badge>
          </HStack>

          <HStack>
            <Text fontWeight="bold">Time:</Text>
            <Text>
              {format(startTime, "h:mm a")} - {format(endTime, "h:mm a")}
            </Text>
          </HStack>

          <HStack>
            <Text fontWeight="bold">Physician:</Text>
            <Text>{physician?.name || "Unknown"}</Text>
          </HStack>

          <HStack>
            <Text fontWeight="bold">Location:</Text>
            <Text>{location?.name || "Unknown"}</Text>
          </HStack>

          <Box height="1px" bg="gray.200" my={2} />

          {isUpcoming && appointment.status === "confirmed" && (
            <HStack justify="flex-end" gap={3}>
              {canCheckIn && (
                <Button
                  colorScheme="green"
                  size="sm"
                  onClick={() => onCheckIn(appointment.id)}
                >
                  Check In
                </Button>
              )}
              <Button
                colorScheme="red"
                variant="outline"
                size="sm"
                onClick={onOpen}
              >
                Cancel Appointment
              </Button>
            </HStack>
          )}
        </VStack>
      </Box>

      {isOpen && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(0, 0, 0, 0.5)"
          zIndex="1000"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            bg="white"
            borderRadius="lg"
            maxW="md"
            w="100%"
            p={4}
            boxShadow="xl"
          >
            <Heading size="md" mb={4}>
              Cancel Appointment
            </Heading>

            <Text mb={4}>
              Are you sure you want to cancel your appointment on{" "}
              {format(startTime, "MMMM d")} at {format(startTime, "h:mm a")}?
              This action cannot be undone.
            </Text>

            <HStack gap={3} justify="flex-end">
              <Button ref={cancelRef} onClick={onClose}>
                Keep Appointment
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  onCancel(appointment.id);
                  onClose();
                }}
              >
                Yes, Cancel
              </Button>
            </HStack>
          </Box>
        </Box>
      )}
    </>
  );
};

export default AppointmentCard;
